// Pure (React-free) logic behind the booking-line forms: the draft <-> API
// payload conversions shared by "add line", the quick-create page and the
// Phase 13.2 edit modal. Kept separate so it is unit-testable without a DOM.

import { isoToZonedWall, zonedWallToIso } from "@/lib/time";
import type {
  BookingLine,
  BookingLineExcursionType,
  BookingLinePaymentCollector,
  BookingLineServiceKind,
  GuidePaymentCollector,
} from "@/lib/types";

export const SERVICE_KINDS: BookingLineServiceKind[] = [
  "transfer",
  "safari",
  "city_tour",
  "yacht",
  "balloon",
  "guide",
  "park_tickets",
  "visa",
  "other",
];

/** Kinds that are a day out with a hotel pickup and a destination emirate
 * (the owner's "excursion / safari / city tour / yacht" group). Yacht has its
 * own field set (see `isYachtKind`/`isGuideKind` in LineFields) even though it
 * is still excursion-shaped for the commission/dispatch lists below. */
export const EXCURSION_KINDS: BookingLineServiceKind[] = ["safari", "city_tour", "yacht", "balloon"];

/** Owner rule 18: an agent's commission is taken only from these services — never from
 * entrance tickets or anything else (mirrors COMMISSIONABLE_SERVICE_KINDS on the server). */
export const COMMISSIONABLE_KINDS: BookingLineServiceKind[] = [
  "city_tour",
  "yacht",
  "transfer",
  "safari",
  "balloon",
  "guide",
];

export function isExcursionKind(kind: string): boolean {
  return (EXCURSION_KINDS as string[]).includes(kind);
}

export function isYachtKind(kind: string): boolean {
  return kind === "yacht";
}

/** Who takes the money unless the owner picks otherwise: the driver, except on a
 * yacht with no marina transfer — nobody is sent, so the client pays NovaMoon
 * directly (mirrors the backend's `default_payment_collector`). */
export function defaultPaymentCollector(
  kind: string,
  yachtTransferNeeded: boolean,
): BookingLinePaymentCollector {
  return isYachtKind(kind) && !yachtTransferNeeded ? "owner_direct" : "driver";
}

export function isGuideKind(kind: string): boolean {
  return kind === "guide";
}

/** Kinds whose form asks «Тип экскурсии» (private up to 4 / up to 6, group)
 * instead of the old Premium / Standard / VIP category (mirrors the backend's
 * `TOUR_TYPE_KINDS`). Their route is just «Место подачи». */
export const TOUR_TYPE_KINDS: BookingLineServiceKind[] = ["safari", "city_tour", "balloon"];

export function isTourTypeKind(kind: string): boolean {
  return (TOUR_TYPE_KINDS as string[]).includes(kind);
}

export function isTicketsKind(kind: string): boolean {
  return kind === "park_tickets";
}

export const EXCURSION_TYPES: BookingLineExcursionType[] = ["private_4", "private_6", "group"];

/** Seats in the car of a private excursion type. */
export const EXCURSION_TYPE_CAPACITY: Record<string, number> = { private_4: 4, private_6: 6 };

/** A safari / city tour / balloon with a «Тип экскурсии» set — an older line has
 * none (its category still applies) until the owner picks one. */
export function hasExcursionType(d: Pick<LineDraft, "service_kind" | "excursion_type">): boolean {
  return isTourTypeKind(d.service_kind) && d.excursion_type !== "";
}

/** Priced «взрослых × цена + детей × цена»: a group excursion, or park tickets
 * (an older ticket line, with neither count, keeps its single price). */
export function pricedPerGuest(
  d: Pick<LineDraft, "service_kind" | "excursion_type" | "adult_count" | "child_count">,
): boolean {
  if (isTourTypeKind(d.service_kind)) return d.excursion_type === "group";
  if (isTicketsKind(d.service_kind)) return d.adult_count.trim() !== "" || d.child_count.trim() !== "";
  return false;
}

/** Adults + children of a per-guest draft. */
export function guestCount(d: Pick<LineDraft, "adult_count" | "child_count">): number {
  return (Number(d.adult_count) || 0) + (Number(d.child_count) || 0);
}

/** A driver of our own (staff or the owner) — «Оплата водителю» defaults to 0 and
 * the money they take from the client is ours at once. */
export function isOwnDriver(d: { is_staff?: boolean; is_owner?: boolean } | undefined): boolean {
  return !!d && (!!d.is_staff || !!d.is_owner);
}

/** The seven emirates plus Musandam/Oman. `value` is what is stored on the
 * line (English — it goes straight onto the driver's card); `key` is the
 * i18n key under `emirates.*`. */
export const EMIRATES: { value: string; key: string }[] = [
  { value: "Dubai", key: "dubai" },
  { value: "Abu Dhabi", key: "abuDhabi" },
  { value: "Sharjah", key: "sharjah" },
  { value: "Ras Al Khaimah", key: "rasAlKhaimah" },
  { value: "Fujairah", key: "fujairah" },
  { value: "Ajman", key: "ajman" },
  { value: "Umm Al Quwain", key: "ummAlQuwain" },
  { value: "Musandam / Oman", key: "musandamOman" },
];

/** Vehicle-capacity presets in the dropdown; anything else is "other". */
export const CAPACITY_PRESETS = ["4", "6", "7"];
export const DEFAULT_VEHICLE_CAPACITY = "6";

/** A transfer's «Тип машины» (migration 0065) and its seats — the pax limit. */
export const VEHICLE_TYPES: { value: string; seats: number }[] = [
  { value: "sedan_4", seats: 4 },
  { value: "minivan_7", seats: 7 },
  { value: "bus_12", seats: 12 },
  { value: "bus_14", seats: 14 },
  { value: "bus_22", seats: 22 },
  { value: "bus_35", seats: 35 },
  { value: "bus_50", seats: 50 },
];
export const DEFAULT_VEHICLE_TYPE = "sedan_4";

export function vehicleTypeSeats(type: string): number | null {
  return VEHICLE_TYPES.find((v) => v.value === type)?.seats ?? null;
}

/** The currencies «Сумма к получению» may be taken in. */
export const COLLECT_CURRENCIES = ["AED", "USD"];

/** "12.50" -> 1250 minor units; 0 for blank; null for a negative/invalid entry. */
export function toMinor(major: string): number | null {
  if (!major.trim()) return 0;
  const n = Number(major.trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** 1250 -> "12.50"; "" for null / 0 (an unset amount, not a literal zero). */
export function fromMinor(minor: number | null | undefined): string {
  if (minor == null || minor === 0) return "";
  return (minor / 100).toFixed(2);
}

export interface LineDraft {
  service_kind: BookingLineServiceKind;
  service_date: string;
  pickup_time: string;
  service_name: string;
  from_location: string;
  from_kind: string;
  from_emirate: string;
  to_location: string;
  to_kind: string;
  to_emirate: string;
  flight_number: string;
  baggage: string;
  /** «Багаж (мест)» — the number of pieces; "" = not said. */
  baggage_count: string;
  pickup_location: string;
  pickup_address: string;
  pax_count: string;
  transport_type: string;
  passenger_name: string;
  /** False while the name is just following the order's customer; flips to
   * true the moment the owner types in the field. Never sent to the API. */
  passenger_name_touched: boolean;
  passenger_whatsapp: string;
  service_category: string;
  /** Safari / city tour / balloon: "private_4" | "private_6" | "group"; "" on an
   * older line that still has its category. */
  excursion_type: string;
  /** Group excursion / park tickets: adults and children (the per-adult prices
   * are `*_per_person`, the per-child ones below). */
  adult_count: string;
  child_count: string;
  supplier_price_per_child: string;
  sale_price_per_child: string;
  supplier_price_per_person: string;
  supplier_price_per_vehicle: string;
  vehicle_capacity: string;
  /** Transfer only: «Тип машины» (`VEHICLE_TYPES`); "" on an older transfer
   * that only has a capacity. */
  vehicle_type: string;
  sale_price_per_person: string;
  sale_price: string;
  currency: string;
  payment_collector: BookingLinePaymentCollector;
  collect_amount: string;
  /** The currency of `collect_amount`; "" = the line's own. */
  collect_currency: string;
  notes: string;
  driver_guide_id: string;
  /** Yacht: rental hours (the hourly-scheme multiplier). Guide: booked hours
   * (informational — a guide's price is a flat total, see `guidePay`). */
  duration_hours: string;
  /** Yacht only: the departure marina/pier. */
  marina_location: string;
  /** Yacht only: whether a transfer to the marina is needed — gates showing
   * pickup_location/pickup_address/from_emirate/to_emirate/baggage. */
  yacht_transfer_needed: boolean;
  /** Guide line, or a transfer's guide («+ Гид»): "ru" / "en" / "other". */
  guide_language: string;
  /** Transfer only (Phase 14.3): «+ Гид» — a guide rides along with the car.
   * Off: the guide block is hidden and none of the guide_* fields is sent as
   * anything but empty / 0, so the guide counts nowhere. */
  guide_included: boolean;
  /** The transfer's guide (Drivers & guides, kind `guide`); optional. */
  guide_driver_id: string;
  guide_hours: string;
  /** What NovaMoon pays the guide — always AED. */
  guide_pay: string;
  /** The guide's price to the client, in the line's currency. */
  guide_sale: string;
  /** Who takes the client's money for the guide — apart from the car's. */
  guide_payment_collector: GuidePaymentCollector;
}

export function emptyLineDraft(currency = "AED", customerName = ""): LineDraft {
  return {
    service_kind: "transfer",
    service_date: "",
    pickup_time: "",
    service_name: "",
    from_location: "",
    from_kind: "",
    from_emirate: "",
    to_location: "",
    to_kind: "",
    to_emirate: "",
    flight_number: "",
    baggage: "",
    baggage_count: "",
    pickup_location: "",
    pickup_address: "",
    pax_count: "",
    transport_type: "private",
    passenger_name: customerName,
    passenger_name_touched: false,
    passenger_whatsapp: "",
    service_category: "premium",
    excursion_type: "private_4",
    adult_count: "1",
    child_count: "0",
    supplier_price_per_child: "",
    sale_price_per_child: "",
    supplier_price_per_person: "",
    supplier_price_per_vehicle: "",
    vehicle_capacity: DEFAULT_VEHICLE_CAPACITY,
    vehicle_type: DEFAULT_VEHICLE_TYPE,
    sale_price_per_person: "",
    sale_price: "",
    currency,
    payment_collector: "driver",
    collect_amount: "",
    collect_currency: "",
    notes: "",
    driver_guide_id: "",
    duration_hours: "",
    marina_location: "",
    yacht_transfer_needed: false,
    guide_language: "",
    ...EMPTY_TRANSFER_GUIDE,
  };
}

/** The «+ Гид» part of a draft with the toggle off. */
export const EMPTY_TRANSFER_GUIDE = {
  guide_included: false,
  guide_driver_id: "",
  guide_hours: "",
  guide_pay: "",
  guide_sale: "",
  guide_payment_collector: "guide" as GuidePaymentCollector,
};

/** Whether this draft carries a transfer's guide («+ Гид» on a transfer). */
export function hasTransferGuide(d: Pick<LineDraft, "service_kind" | "guide_included">): boolean {
  return d.service_kind === "transfer" && d.guide_included;
}

/** Make every draft that has not had its tourist name typed in follow the
 * order's customer name (the quick-create page calls this when the customer
 * changes). */
export function withCustomerName(draft: LineDraft, customerName: string): LineDraft {
  return draft.passenger_name_touched ? draft : { ...draft, passenger_name: customerName };
}

/** "YYYY-MM-DD" part of a "YYYY-MM-DDTHH:mm" string. */
function datePart(dt: string): string {
  return dt.slice(0, 10);
}

/** Keep a picked pickup time on the same day as the service date.
 *
 * Called when the service date changes: if the pickup time was in step with
 * the *old* date (or there was no old date yet) it is moved to the new date,
 * keeping the clock time. A pickup deliberately on another day (a
 * just-after-midnight airport run) is left alone. */
export function rebasePickupTime(
  pickupTime: string,
  oldDate: string,
  newDate: string,
): string {
  if (!pickupTime || !newDate) return pickupTime;
  const inStep = !oldDate || datePart(pickupTime) === oldDate;
  if (!inStep) return pickupTime;
  return `${newDate}${pickupTime.slice(10)}`;
}

export type LineDraftProblem =
  | "date"
  | "price"
  | "capacity"
  | "baggage"
  | "pax_over_capacity"
  | "hours"
  | "guide_price"
  | "guests";

function isSharingScheme(d: Pick<LineDraft, "transport_type" | "service_kind">): boolean {
  return (
    d.transport_type === "sharing" &&
    d.service_kind !== "transfer" &&
    !isYachtKind(d.service_kind) &&
    !isGuideKind(d.service_kind)
  );
}

/** The first thing wrong with a draft, or null when it can be saved. */
export function lineDraftProblem(d: LineDraft): LineDraftProblem | null {
  if (d.service_date.trim() === "") return "date";
  if (isYachtKind(d.service_kind)) {
    const hours = Number(d.duration_hours);
    if (!d.duration_hours.trim() || !Number.isFinite(hours) || hours < 1) return "hours";
    if (toMinor(d.sale_price_per_person) === null) return "price";
    const cap = d.vehicle_capacity.trim();
    if (!/^[1-9]\d*$/.test(cap)) return "capacity";
    const pax = d.pax_count.trim();
    if (pax && Number(pax) > Number(cap)) return "pax_over_capacity";
    return null;
  }
  if (isGuideKind(d.service_kind)) {
    return toMinor(d.sale_price) === null ? "price" : null;
  }
  if (pricedPerGuest(d)) {
    const counts = [d.adult_count, d.child_count];
    if (counts.some((c) => c.trim() !== "" && !/^\d+$/.test(c.trim())) || guestCount(d) < 1) {
      return "guests";
    }
    const prices = [
      d.sale_price_per_person,
      d.sale_price_per_child,
      d.supplier_price_per_person,
      d.supplier_price_per_child,
    ];
    return prices.some((p) => toMinor(p) === null) ? "price" : null;
  }
  if (hasExcursionType(d)) {
    if (toMinor(d.sale_price) === null) return "price";
    const pax = d.pax_count.trim();
    if (pax && Number(pax) > EXCURSION_TYPE_CAPACITY[d.excursion_type]) return "pax_over_capacity";
    return null;
  }
  if (isSharingScheme(d)) {
    return toMinor(d.sale_price_per_person) === null ? "price" : null;
  }
  if (toMinor(d.sale_price) === null) return "price";
  if (hasTransferGuide(d) && (toMinor(d.guide_pay) === null || toMinor(d.guide_sale) === null)) {
    return "guide_price";
  }
  if (!/^\d*$/.test(d.baggage_count.trim())) return "baggage";
  const seats = d.service_kind === "transfer" ? vehicleTypeSeats(d.vehicle_type) : null;
  if (seats !== null) {
    const pax = d.pax_count.trim();
    return pax && Number(pax) > seats ? "pax_over_capacity" : null;
  }
  const cap = d.vehicle_capacity.trim();
  if (!/^[1-9]\d*$/.test(cap)) return "capacity";
  const pax = d.pax_count.trim();
  if (pax && Number(pax) > Number(cap)) return "pax_over_capacity";
  return null;
}

export function lineDraftIsValid(d: LineDraft): boolean {
  return lineDraftProblem(d) === null;
}

/** Draft -> the create-line JSON payload (POST .../lines and .../bookings/quick). */
export function lineDraftToPayload(d: LineDraft, defaultCurrency: string) {
  const perGuest = pricedPerGuest(d);
  const typed = hasExcursionType(d);
  return {
    service_kind: d.service_kind,
    service_date: d.service_date,
    // Typed as Dubai wall-clock time, whatever the browser's own zone is.
    pickup_time: d.pickup_time ? zonedWallToIso(d.pickup_time) : null,
    service_name: d.service_name || null,
    from_location: d.from_location || null,
    from_kind: d.from_kind || null,
    from_emirate: d.from_emirate || null,
    to_location: d.to_location || null,
    to_kind: d.to_kind || null,
    to_emirate: d.to_emirate || null,
    flight_number: d.flight_number || null,
    baggage: d.baggage || null,
    baggage_count: d.baggage_count.trim() ? Number(d.baggage_count) : null,
    pickup_location: d.pickup_location || null,
    pickup_address: d.pickup_address || null,
    pax_count: d.pax_count.trim() ? Number(d.pax_count) : null,
    // A typed excursion / tickets fix the scheme: group & tickets are per guest
    // ("sharing"), a private type is one price for the car.
    transport_type: perGuest ? "sharing" : typed ? "private" : d.transport_type || null,
    passenger_name: d.passenger_name || null,
    passenger_whatsapp: d.passenger_whatsapp || null,
    // «Тип экскурсии» / «Парк / аттракцион» replace the old category there.
    service_category:
      d.service_kind === "transfer" || typed || isTicketsKind(d.service_kind)
        ? null
        : d.service_category || null,
    excursion_type: typed ? d.excursion_type : null,
    adult_count: perGuest ? Number(d.adult_count) || 0 : null,
    child_count: perGuest ? Number(d.child_count) || 0 : null,
    supplier_price_per_child_minor: perGuest ? toMinor(d.supplier_price_per_child) ?? 0 : null,
    sale_price_per_child_minor: perGuest ? toMinor(d.sale_price_per_child) ?? 0 : null,
    supplier_price_per_person_minor: toMinor(d.supplier_price_per_person) ?? 0,
    supplier_price_per_vehicle_minor: toMinor(d.supplier_price_per_vehicle) ?? 0,
    vehicle_type: transferVehicleType(d),
    vehicle_capacity:
      typed && !perGuest
        ? EXCURSION_TYPE_CAPACITY[d.excursion_type]
        : transferVehicleType(d)
          ? vehicleTypeSeats(d.vehicle_type)
          : d.vehicle_capacity.trim()
          ? Number(d.vehicle_capacity)
          : Number(DEFAULT_VEHICLE_CAPACITY),
    sale_price_per_person_minor: toMinor(d.sale_price_per_person) ?? 0,
    sale_price_minor: toMinor(d.sale_price) ?? 0,
    currency: d.currency || defaultCurrency,
    payment_collector: d.payment_collector,
    collect_amount_minor: toMinor(d.collect_amount) || null,
    collect_currency: collectCurrency(d, defaultCurrency),
    notes: d.notes || null,
    driver_guide_id: d.driver_guide_id || null,
    duration_hours: d.duration_hours.trim() ? Number(d.duration_hours) : null,
    marina_location: d.marina_location || null,
    yacht_transfer_needed: d.yacht_transfer_needed,
    // A transfer's language belongs to its guide — none without «+ Гид».
    guide_language:
      d.service_kind === "transfer" && !d.guide_included ? null : d.guide_language || null,
    ...transferGuidePayload(d),
  };
}

function transferVehicleType(d: LineDraft): string | null {
  return d.service_kind === "transfer" && vehicleTypeSeats(d.vehicle_type) !== null
    ? d.vehicle_type
    : null;
}

/** «Сумма к получению» in a currency other than the line's — only with an
 * amount typed (a blank amount is the line's price, in the line's currency). */
function collectCurrency(d: LineDraft, defaultCurrency: string): string | null {
  const line = d.currency || defaultCurrency;
  if (d.payment_collector !== "driver" || !toMinor(d.collect_amount)) return null;
  return d.collect_currency && d.collect_currency !== line ? d.collect_currency : null;
}

/** The «+ Гид» fields of the payload — all empty / 0 unless a transfer has it on. */
function transferGuidePayload(d: LineDraft) {
  if (!hasTransferGuide(d)) {
    return {
      guide_included: false,
      guide_driver_id: null,
      guide_hours: null,
      guide_pay_minor: 0,
      guide_sale_minor: 0,
      guide_payment_collector: null,
    };
  }
  return {
    guide_included: true,
    guide_driver_id: d.guide_driver_id || null,
    guide_hours: d.guide_hours.trim() ? Number(d.guide_hours) : null,
    guide_pay_minor: toMinor(d.guide_pay) ?? 0,
    guide_sale_minor: toMinor(d.guide_sale) ?? 0,
    guide_payment_collector: d.guide_payment_collector,
  };
}

/** A saved line -> an editable draft. A blank tourist name falls back to the
 * order's customer name (the owner can overwrite it). */
export function lineToDraft(line: BookingLine, customerName = ""): LineDraft {
  return {
    service_kind: line.service_kind,
    service_date: line.service_date,
    // The Dubai wall clock the driver works to, not the browser's.
    pickup_time: isoToZonedWall(line.pickup_time),
    service_name: line.service_name ?? "",
    from_location: line.from_location ?? "",
    from_kind: line.from_kind ?? "",
    from_emirate: line.from_emirate ?? "",
    to_location: line.to_location ?? "",
    to_kind: line.to_kind ?? "",
    to_emirate: line.to_emirate ?? "",
    flight_number: line.flight_number ?? "",
    baggage: line.baggage ?? "",
    baggage_count: line.baggage_count == null ? "" : String(line.baggage_count),
    pickup_location: line.pickup_location ?? "",
    pickup_address: line.pickup_address ?? "",
    pax_count: line.pax_count == null ? "" : String(line.pax_count),
    transport_type: line.transport_type ?? "private",
    passenger_name: line.passenger_name ?? customerName,
    passenger_name_touched: true,
    passenger_whatsapp: line.passenger_whatsapp ?? "",
    service_category: line.service_category ?? "premium",
    excursion_type: line.excursion_type ?? "",
    adult_count: line.adult_count == null ? "" : String(line.adult_count),
    child_count: line.child_count == null ? "" : String(line.child_count),
    supplier_price_per_child: fromMinor(line.supplier_price_per_child_minor),
    sale_price_per_child: fromMinor(line.sale_price_per_child_minor),
    supplier_price_per_person: fromMinor(line.supplier_price_per_person_minor),
    supplier_price_per_vehicle: fromMinor(line.supplier_price_per_vehicle_minor),
    vehicle_capacity: String(line.vehicle_capacity ?? DEFAULT_VEHICLE_CAPACITY),
    vehicle_type: line.vehicle_type ?? "",
    sale_price_per_person: fromMinor(line.sale_price_per_person_minor),
    sale_price: fromMinor(line.sale_price_minor),
    currency: line.currency,
    payment_collector: line.payment_collector,
    // An amount that just follows the price is shown as the default (blank) —
    // the server keeps it in step with a price edit.
    collect_amount:
      line.collect_amount_minor === line.sale_price_minor && !line.collect_currency
        ? ""
        : fromMinor(line.collect_amount_minor),
    collect_currency: line.collect_currency ?? "",
    notes: line.notes ?? "",
    driver_guide_id: "",
    duration_hours: line.duration_hours == null ? "" : String(line.duration_hours),
    marina_location: line.marina_location ?? "",
    yacht_transfer_needed: line.yacht_transfer_needed ?? false,
    guide_language: line.guide_language ?? "",
    guide_included: line.guide_included ?? false,
    guide_driver_id: line.guide_driver_id ?? "",
    guide_hours: line.guide_hours == null ? "" : String(line.guide_hours),
    guide_pay: fromMinor(line.guide_pay_minor),
    guide_sale: fromMinor(line.guide_sale_minor),
    guide_payment_collector: line.guide_payment_collector ?? "guide",
  };
}

/** Only the fields the owner actually changed, as a PATCH body. Both sides go
 * through `lineDraftToPayload`, so a value that merely round-trips (a blank
 * that becomes `null`, "150.00" that becomes 15000) is never a phantom edit.
 * `driver_guide_id` is create-only and never part of a PATCH. */
export function lineDraftToPatch(
  original: LineDraft,
  current: LineDraft,
  defaultCurrency: string,
): Record<string, unknown> {
  const a = lineDraftToPayload(original, defaultCurrency) as Record<string, unknown>;
  const b = lineDraftToPayload(current, defaultCurrency) as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(b)) {
    if (key === "driver_guide_id") continue;
    if (a[key] !== b[key]) patch[key] = b[key];
  }
  return patch;
}

/** Which supplier field is "what we pay the driver" for this line: the
 * per-person rate on a sharing excursion, otherwise the per-vehicle price. */
export function driverPayField(
  line: Pick<BookingLine, "service_kind" | "transport_type">,
): "supplier_price_per_person_minor" | "supplier_price_per_vehicle_minor" {
  return isSharingScheme({
    service_kind: line.service_kind,
    transport_type: line.transport_type ?? "private",
  })
    ? "supplier_price_per_person_minor"
    : "supplier_price_per_vehicle_minor";
}
